import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';
import { AdminUser, UsersPagination } from '@/types';
import {
  Users as UsersIcon,
  RefreshCw,
  Search,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const USER_TYPE_FILTERS = [
  { value: 'patient', label: 'Patients' },
  { value: 'all', label: 'All users' },
];

const PROGRESS_STEPS: { key: keyof NonNullable<AdminUser['progress']> | 'signedUp'; label: string }[] = [
  { key: 'signedUp', label: 'Signed up' },
  { key: 'emailConfirmed', label: 'Confirmed' },
  { key: 'startedConsultation', label: 'Consultation' },
  { key: 'paid', label: 'Paid' },
];

/** Signed up -> confirmed -> consultation -> paid, from the database. */
function ProgressSteps({ progress }: { progress?: AdminUser['progress'] }) {
  if (!progress) return <span className="text-gray-400">—</span>;
  const reached = PROGRESS_STEPS.filter((s) => s.key === 'signedUp' || progress[s.key as keyof typeof progress]);
  const furthest = reached[reached.length - 1];
  return (
    <div className="flex items-center gap-1" title={PROGRESS_STEPS.map((s) => `${reached.includes(s) ? '✓' : '·'} ${s.label}`).join('\n')}>
      {PROGRESS_STEPS.map((s) => (
        <span
          key={s.key}
          className={`h-1.5 w-5 rounded-full ${reached.includes(s) ? (progress.paid ? 'bg-green-500' : 'bg-primary-500') : 'bg-gray-200'}`}
        />
      ))}
      <span className={`ml-1.5 font-medium ${progress.paid ? 'text-green-700' : 'text-gray-600'}`}>{furthest.label}</span>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState<UsersPagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [userType, setUserType] = useState<string>('patient');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const pageSize = 20;

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await api.getUsers({ page, pageSize, userType, search: search || undefined });
      setUsers(data.users || []);
      setPagination(data.pagination || null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, userType, search]);

  // Reset to first page whenever the filter or search term changes.
  useEffect(() => {
    setPage(1);
  }, [userType, search]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fullName = (user: AdminUser) => {
    const name = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ');
    return name || '—';
  };

  const totalCount = pagination?.totalCount ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">Browse and search registered users</p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            onClick={fetchUsers}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters + search */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
          <div className="flex flex-wrap gap-2">
            {USER_TYPE_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setUserType(filter.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  userType === filter.value
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <form onSubmit={handleSearchSubmit} className="relative w-full md:w-80">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-10 pr-20 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No users found</h3>
          <p className="mt-2 text-gray-500">
            {search ? 'No users match your search.' : 'There are no users to display yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last login
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 font-medium text-sm">
                            {(user.profile?.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900">{fullName(user)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                        {user.userType || 'unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.isActive ? (
                        <span className="inline-flex items-center text-xs font-medium text-green-700">
                          <CheckCircle size={14} className="mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-medium text-gray-500">
                          <XCircle size={14} className="mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {user.acquisitionSource ? (
                        user.visitorId ? (
                          <Link
                            to={`/analytics/sessions?visitor_id=${encodeURIComponent(user.visitorId)}`}
                            className="text-primary-700 hover:underline"
                            title="Open this visitor's session timeline"
                          >
                            {user.acquisitionSource}
                          </Link>
                        ) : (
                          <span className="text-gray-700">{user.acquisitionSource}</span>
                        )
                      ) : (
                        <span className="text-gray-400" title="Signed up before sources were saved, or arrived directly">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <ProgressSteps progress={user.progress} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.createdAt)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.lastLogin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && users.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing{' '}
            <span className="font-medium">{(page - 1) * pageSize + 1}</span>–
            <span className="font-medium">{Math.min(page * pageSize, totalCount)}</span> of{' '}
            <span className="font-medium">{totalCount}</span> users
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} className="mr-1" />
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
